import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Headphones, CheckCircle2, X, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { startRingback, stopRingback } from "@/lib/ringback";

/* Screen 5 — Matchmaking.
   REAL server-side pairing:
   - Every ~2s we POST /api/matchmaking/heartbeat. The server registers the
     caller in a queue and, if any other user is waiting, mints a unique
     `match-<uuid>` room and returns it to both parties.
   - We navigate to /video-call with that room in location.state so BOTH
     paired users join the SAME LiveKit room.
   - Cancel + unmount call /api/matchmaking/leave. Stale entries also expire
     server-side after 15s of no heartbeat. */

const POLL_MS = 2000;
const MATCH_HOLD_MS = 900;

export default function Matchmaking() {
  const navigate = useNavigate();
  const location = useLocation();
  const [matched, setMatched] = useState(false);
  const cancelledRef = useRef(false);
  // Survives StrictMode's double-mount (refs persist across the remount).
  // If the ZOMBIE loop's heartbeat is the one that pairs, its "matched"
  // response is consumed server-side but swallowed client-side (alive=false)
  // — without this stash the surviving loop would poll "waiting" forever and
  // the match would be lost. Both loops check this ref before anything else.
  const matchRef = useRef(null);
  // Device choices from the Pre-Call screen (optional — undefined means "let
  // LiveKit pick"). We only forward them, never mutate.
  const videoDeviceId = location.state?.videoDeviceId || "";
  const audioDeviceId = location.state?.audioDeviceId || "";
  // Camera preference from Practice Setup (state first, storage fallback).
  const cameraOn =
    location.state?.cameraOn ??
    localStorage.getItem("salespit.camera_on") !== "0";
  // Practice context written by Practice Setup — attached to every heartbeat
  // so the server can hand it to our matched partner.
  const practiceContext = (() => {
    try {
      const raw = localStorage.getItem("salespit.practice_context");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  // Camera Off = realistic phone dialler simulation.
  const dialler = !cameraOn;

  // UK double-ring ringback while searching in phone mode. The AudioContext
  // itself was gesture-unlocked on the Pre-Call "Start Matching" tap (iOS
  // requirement — see lib/ringback.js). The loop starts once, runs while
  // searching, and stops instantly when `matched` flips, on cancel, or on
  // unmount (effect cleanup).
  useEffect(() => {
    if (!dialler || matched) return undefined;
    startRingback();
    return () => stopRingback();
  }, [dialler, matched]);

  // No `useRef` guard against StrictMode double-mount here — that was the
  // exact bug that stopped this page from ever pairing users. Instead we
  // use a per-effect local `alive` flag and a fresh `inFlight` closure var.

  useEffect(() => {
    let timeoutId = null;
    let alive = true;
    let inFlight = false;

    const beat = async () => {
      if (!alive || cancelledRef.current || inFlight) return;
      inFlight = true;
      try {
        const r = await api.post("/matchmaking/heartbeat", {
          context: practiceContext,
          call_mode: cameraOn ? "video" : "phone",
        });
        inFlight = false;
        // Capture a match BEFORE the zombie-loop bail-out so a swallowed
        // response is still honoured by the surviving loop (see matchRef).
        if (r.data?.status === "matched" && r.data?.room_name) {
          matchRef.current = r.data;
        }
        if (!alive || cancelledRef.current) return;
        const m = matchRef.current;
        if (m) {
          setMatched(true);
          setTimeout(() => {
            if (cancelledRef.current) return;
            // Stash the match payload for the Briefing screen — sessionStorage
            // survives a refresh, so context is never lost mid-flow.
            sessionStorage.setItem(
              "salespit.matched_payload",
              JSON.stringify({
                room: m.room_name,
                partner_name: m.partner_name || null,
                partner_context: m.partner_context || null,
                your_role: m.your_role || null, // server-assigned: rep|prospect
                cameraOn,
                videoDeviceId,
                audioDeviceId,
              })
            );
            navigate("/briefing", { replace: true });
          }, MATCH_HOLD_MS);
          return; // stop polling
        }
        timeoutId = setTimeout(beat, POLL_MS);
      } catch (err) {
        inFlight = false;
        console.warn("[matchmaking] heartbeat failed", err?.message || err);
        if (!alive || cancelledRef.current) return;
        timeoutId = setTimeout(beat, POLL_MS);
      }
    };

    // A previous zombie loop may have already captured the match.
    if (matchRef.current) {
      setMatched(true);
    }
    beat();

    return () => {
      alive = false;
      if (timeoutId) clearTimeout(timeoutId);
      // NOTE: we deliberately DO NOT call /matchmaking/leave on cleanup.
      // React 18 StrictMode double-mounts every effect in dev — leaving
      // here would immediately evict us from the queue right after the
      // first heartbeat, and the remount's fresh queue slot could then
      // never be paired. Server-side stale purge (15 s) is what handles
      // real page-close. Explicit cancel calls /leave from handleCancel.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    cancelledRef.current = true;
    api.post("/matchmaking/leave").catch(() => {});
    toast("Search cancelled.");
    navigate("/homepage");
  };

  return (
    <main
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0A0A0A] px-6 font-body text-white"
      data-testid="matchmaking-page"
    >
      {/* ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.14),transparent_62%)] blur-3xl"
      />

      {/* Animated searcher */}
      <div className="relative z-10 flex h-52 w-52 items-center justify-center sm:h-64 sm:w-64">
        {!matched &&
          [0, 1, 2].map((i) => (
            <motion.span
              key={i}
              aria-hidden="true"
              className="absolute h-40 w-40 rounded-full border border-[#E52525]/40 sm:h-48 sm:w-48"
              initial={{ scale: 1, opacity: 0.55 }}
              animate={{ scale: 2.1, opacity: 0 }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                delay: i * 0.85,
                ease: "easeOut",
              }}
            />
          ))}

        {!matched && (
          <motion.span
            aria-hidden="true"
            className="absolute h-44 w-44 rounded-full border-2 border-transparent border-t-[#E52525] sm:h-52 sm:w-52"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
          />
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={matched ? "found" : "searching"}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={`relative flex h-40 w-40 items-center justify-center rounded-full border sm:h-48 sm:w-48 ${
              matched
                ? "border-[#22C55E]/40 bg-[#22C55E]/10"
                : "border-[#E52525]/30 bg-[#E52525]/10"
            }`}
          >
            {matched ? (
              <CheckCircle2 className="text-[#22C55E]" size={64} />
            ) : (
              <motion.span
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              >
                {dialler ? (
                  <Phone className="text-[#E52525]" size={60} />
                ) : (
                  <Headphones className="text-[#E52525]" size={60} />
                )}
              </motion.span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Text */}
      <div className="relative z-10 mt-14 max-w-md text-center">
        <AnimatePresence mode="wait">
          <motion.h1
            key={matched ? "h-found" : "h-search"}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl"
            data-testid="matchmaking-title"
          >
            {matched
              ? dialler
                ? "Connected!"
                : "Match found!"
              : dialler
                ? "Ringing…"
                : "Finding another sales rep…"}
          </motion.h1>
        </AnimatePresence>
        <p
          className="mt-4 text-base font-light leading-relaxed text-zinc-400"
          data-testid="matchmaking-status-text"
        >
          {matched
            ? dialler
              ? "You're connected — review the scenarios before the call begins."
              : "Connecting you to your live mock call…"
            : dialler
              ? "Simulated phone call — we're dialling another rep for you. It stops ringing the moment you're connected."
              : "SalesPit is searching for the best available practice partner for you. This usually takes just a few seconds."}
        </p>
      </div>

      {/* Cancel */}
      {!matched && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          data-testid="cancel-search-btn"
          onClick={handleCancel}
          className="relative z-10 mt-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#E52525] px-8 py-3.5 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.3)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-95"
        >
          <X size={18} />
          Cancel Search
        </motion.button>
      )}
    </main>
  );
}
